// shared/utils/mergeRunFrames.js
//
// Accumulating a run out of the frames that stream in.
//
// A frame is not a snapshot of the whole run. When one would exceed the 128 KB
// WebSocket limit the executor shrinks it, in this order, stopping as soon as
// it fits:
//
//   1. shrink the largest bodies      (every step still present, bodies shorter)
//   2. drop already-completed steps   (steps we have seen simply absent)
//   3. drop bodies entirely           (bodyTruncated set, bodies null)
//   4. send the summary alone         (permutations: [])
//
// Every one of those re-sends something we have already been given, carrying
// less than it did the first time. So the rule here is that a frame may only
// ever add: steps and permutations are unioned by identity, fields are merged
// keeping the fullest value seen, and nothing that has arrived is thrown away
// by a later, thinner frame.
//
// Shared by the store (which caches across frames) and the run panel (which
// merges the cache back over the latest frame for display), because two
// implementations of this drifted apart once already.

/**
 * Whether a value is worth keeping. `false` and `0` are real values; null,
 * empty strings and empty containers are what a shrunken frame sends in place
 * of the data it dropped.
 */
export const hasValue = (v) => {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
};

/**
 * Merge one record field by field, keeping what we already have wherever the
 * incoming frame carries nothing. Status-ish fields change legitimately and
 * the incoming value wins whenever it is real.
 */
export const mergeRecord = (cached, incoming) => {
  if (!cached) return incoming;
  if (!incoming) return cached;

  const merged = { ...cached };
  Object.keys(incoming).forEach((key) => {
    if (hasValue(incoming[key]) || !hasValue(cached[key])) {
      merged[key] = incoming[key];
    }
  });
  return merged;
};

const BODY_FIELDS = ['requestBody', 'responseBody'];

/**
 * One step, merged so the fullest version of every field survives — including
 * a body that a later frame shrank or dropped to fit inside the limit.
 */
export const mergeStep = (cached, incoming) => {
  if (!cached) return incoming;
  if (!incoming) return cached;

  const merged = mergeRecord(cached, incoming);

  // A shrunken body is shorter than the one already in hand. Keep the longer
  // one, and let `bodyTruncated` describe the body actually on display rather
  // than the one that was dropped on the way here.
  let keptFullerBody = false;
  BODY_FIELDS.forEach((field) => {
    const before = cached[field];
    const after = incoming[field];
    const droppedOrShrank =
      typeof before === 'string' &&
      (!hasValue(after) || (typeof after === 'string' && after.length < before.length));

    if (droppedOrShrank) {
      merged[field] = before;
      keptFullerBody = true;
    }
  });
  if (keptFullerBody && !cached.bodyTruncated) merged.bodyTruncated = false;

  return merged;
};

/** Identity of a step across frames — alias plus name, as the stream keys them. */
export const stepKey = (step, index) => {
  const alias = step?.alias ?? '';
  const name = step?.name ?? '';
  return alias || name ? `${alias}|${name}` : `#${index}`;
};

/**
 * Union two step lists by identity, merging the ones both sides have.
 * Existing order is preserved and genuinely new steps are appended, so the
 * list only ever grows within a run.
 */
export const mergeSteps = (cached, incoming) => {
  const have = Array.isArray(cached) ? cached : [];
  const next = Array.isArray(incoming) ? incoming : [];
  if (!next.length) return have;
  if (!have.length) return next;

  const order = have.map(stepKey);
  const byKey = new Map(have.map((step, i) => [stepKey(step, i), step]));

  next.forEach((step, i) => {
    const key = stepKey(step, i);
    if (byKey.has(key)) byKey.set(key, mergeStep(byKey.get(key), step));
    else {
      byKey.set(key, step);
      order.push(key);
    }
  });

  return order.map((key) => byKey.get(key));
};

/** The key a permutation is cached under — its index, or its position. */
export const permKeyOf = (perm, index) =>
  perm?.index != null ? String(perm.index) : String(index);

/**
 * Union two permutation lists, merging each permutation's own fields and its
 * steps. This is what keeps the table populated when a frame arrives with
 * `permutations: []` — stage 4 above, which carries real progress and status
 * and no permutations at all.
 *
 * Ordering is by permutation index where there is one, so a permutation that
 * only reappears later does not jump to the end.
 */
export const mergePermutations = (cached, incoming) => {
  const have = Array.isArray(cached) ? cached : [];
  const next = Array.isArray(incoming) ? incoming : [];
  if (!have.length) return next;
  if (!next.length) return have;

  const byKey = new Map();
  const order = [];

  const take = (perm, i) => {
    const key = permKeyOf(perm, i);
    if (!byKey.has(key)) {
      byKey.set(key, perm);
      order.push(key);
      return;
    }
    const before = byKey.get(key);
    byKey.set(key, {
      ...mergeRecord(before, perm),
      steps: mergeSteps(before.steps, perm.steps),
    });
  };

  have.forEach(take);
  next.forEach(take);

  const numeric = order.every((key) => /^\d+$/.test(key));
  const ordered = numeric ? [...order].sort((a, b) => Number(a) - Number(b)) : order;

  return ordered.map((key) => byKey.get(key));
};
